import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { usePack, useTracks } from "../api/queries";
import type { AnswerValue, Level, PackResponse } from "../api/schemas";
import { DifficultyCurve } from "../components/charts/charts";
import { Button, CategoryTag, Chip, EmptyState, ErrorState, Loading, PageHeader, Stat } from "../components/ui";
import { Card } from "../components/ui";
import { tileClass } from "../lib/catalog";
import { fmtInt, fmtMs, typeSlug } from "../lib/format";
import { useHotkey } from "../lib/hotkeys";
import { canonicalAnswer, PuzzleView, useDigestVerification, type DigestStatus } from "@lumen/puzzle-render";
import styles from "./pages.module.css";

/** docs/levels.md target curve, drawn as a reference. */
const SAW = [0, 0.02, 0.04, 0.06, -0.03, 0.05, 0.07, 0.09, 0.03, 0.14];
const target = (n: number) => 0.12 + 0.7 * (1 - Math.exp(-n / 140)) + SAW[(n - 1) % 10]!;

export function PackPage() {
  const { track = "", index: indexParam = "0" } = useParams();
  const index = Number(indexParam);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q = usePack(track, Number.isInteger(index) ? index : undefined);
  const tracks = useTracks();
  const summary = tracks.data?.tracks.find((t) => t.id === track);
  const selectedN = params.get("level") ? Number(params.get("level")) : null;

  const levels = q.data?.pack.levels ?? [];
  const selected = levels.find((l) => l.n === selectedN) ?? null;
  const select = (n: number | null) => setParams(n ? { level: String(n) } : {}, { replace: true });

  useHotkey("ArrowRight", () => levels.length && select(Math.min(levels.at(-1)!.n, (selectedN ?? levels[0]!.n - 1) + 1)), levels.length > 0);
  useHotkey("ArrowLeft", () => levels.length && select(Math.max(levels[0]!.n, (selectedN ?? levels[0]!.n + 1) - 1)), levels.length > 0);
  useHotkey("Escape", () => select(null), selected !== null);

  const packs = summary?.packs ?? [];
  const title = (
    <span className={styles.packTitle}>
      <CategoryTag category={track} />
      <span>Pack {Number.isInteger(index) ? index : "?"}</span>
      {q.data ? <span className={styles.faint}>· {q.data.pack.chapter.name}</span> : null}
    </span>
  );

  const header = (
    <PageHeader
      title={title}
      meta={
        <>
          <Link to="/levels" className={styles.back}>
            ← Levels
          </Link>
          {q.data ? (
            <>
              <span className="mono">
                L{levels[0]?.n}–L{levels.at(-1)?.n}
              </span>
              <Chip tone="violet">{q.data.pack.chapter.palette}</Chip>
              {q.data.pack.generator ? <span className="mono">{q.data.pack.generator}</span> : null}
              {q.data.source === "studio" ? (
                <Chip tone="accent" title={q.data.studio?.publishedAt ? `Published ${q.data.studio.publishedAt}` : undefined}>
                  {`studio${q.data.studio?.publishedBy ? ` · ${q.data.studio.publishedBy}` : ""}`}
                </Chip>
              ) : null}
              {q.data.unrecovered.length ? (
                <Chip tone="danger" title={q.data.unrecovered.join(", ")}>
                  {q.data.unrecovered.length} unrecovered
                </Chip>
              ) : (
                <Chip tone="success">all answers recovered</Chip>
              )}
              {summary?.packs.find((p) => p.index === index)?.sha256 ? (
                <span className="mono" title={summary.packs.find((p) => p.index === index)!.sha256}>
                  sha256 {summary.packs.find((p) => p.index === index)!.sha256.slice(0, 12)}…
                </span>
              ) : null}
            </>
          ) : null}
        </>
      }
      actions={
        packs.length > 1 ? (
          <div className={styles.packPager}>
            {packs.map((p) => (
              <Button
                key={p.index}
                size="sm"
                variant={p.index === index ? "primary" : "ghost"}
                onClick={() => navigate(`/levels/${track}/${p.index}`)}
                aria-current={p.index === index ? "page" : undefined}
                title={`${p.chapter} · L${p.first}–L${p.last}`}
              >
                Pack {p.index}
              </Button>
            ))}
          </div>
        ) : undefined
      }
    />
  );

  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState
          error={q.error}
          title={(q.error as { status?: number }).status === 404 ? "No such pack" : "Couldn't load this pack"}
          onRetry={() => void q.refetch()}
        />
      </>
    );

  const data = q.data;
  const reached = summary?.maxLevelReached ?? 0;
  const gates = levels.filter((l) => l.flags?.includes("gate")).length;
  const intros = levels.filter((l) => l.flags?.includes("intro")).length;
  const avg = levels.reduce((s, l) => s + l.spec.difficulty.score, 0) / Math.max(1, levels.length);

  return (
    <>
      {header}
      <Card
        title="Difficulty curve"
        actions={
          <span className={styles.curveStats}>
            <span className="mono">avg {avg.toFixed(2)}</span>
            <span className="mono">{gates} gates</span>
            <span className="mono">{intros} intros</span>
          </span>
        }
      >
        <DifficultyCurve
          points={levels.map((l) => ({ n: l.n, score: l.spec.difficulty.score, flags: l.flags ?? [], label: l.spec.type }))}
          selected={selectedN}
          onSelect={select}
          target={target}
        />
      </Card>

      <div className={styles.packGrid} data-open={selected ? true : undefined}>
        <div className={styles.levelGrid} role="list" aria-label="Levels">
          {levels.map((l) => (
            <LevelTile key={l.n} level={l} stats={data.stats[l.spec.id]} selected={l.n === selectedN} reached={l.n <= reached} onSelect={() => select(l.n === selectedN ? null : l.n)} />
          ))}
        </div>
        {selected ? (
          <LevelPreview
            key={selected.n}
            level={selected}
            palette={data.pack.chapter.palette}
            answer={data.answers[selected.spec.id] ?? null}
            stats={data.stats[selected.spec.id]}
            onClose={() => select(null)}
          />
        ) : (
          <div className={styles.previewHint}>
            <EmptyState title="Pick a level">
              Click a level or a point on the curve to preview it with its answer. <kbd>←</kbd> <kbd>→</kbd> step through levels.
            </EmptyState>
          </div>
        )}
      </div>
    </>
  );
}

function LevelTile({
  level,
  stats,
  selected,
  reached,
  onSelect,
}: {
  level: Level;
  stats: PackResponse["stats"][string] | undefined;
  selected: boolean;
  reached: boolean;
  onSelect: () => void;
}) {
  const flags = level.flags ?? [];
  return (
    <button
      type="button"
      role="listitem"
      className={styles.levelTile}
      data-selected={selected || undefined}
      data-gate={flags.includes("gate") || undefined}
      data-unreached={!reached || undefined}
      aria-pressed={selected}
      aria-label={`Level ${level.n}, ${level.spec.type}, difficulty ${level.spec.difficulty.score.toFixed(2)}${flags.length ? `, ${flags.join(", ")}` : ""}`}
      onClick={onSelect}
    >
      <span className={styles.levelTop}>
        <span className={styles.levelN}>{level.n}</span>
        <span className={styles.levelFlags}>
          {flags.includes("intro") ? <span data-flag="intro" title="intro" /> : null}
          {flags.includes("relief") ? <span data-flag="relief" title="relief" /> : null}
          {flags.includes("gate") ? <span data-flag="gate" title="gate" /> : null}
          {flags.includes("chapterEnd") ? <span data-flag="chapterEnd" title="chapter end" /> : null}
        </span>
      </span>
      <span className={styles.levelType}>{typeSlug(level.spec.type)}</span>
      <span className={styles.levelBar}>
        <span style={{ width: `${level.spec.difficulty.score * 100}%` }} />
      </span>
      <span className={styles.levelMeta}>
        <span className="mono">{level.spec.difficulty.score.toFixed(2)}</span>
        <span className="mono">{stats ? fmtInt(stats.completions) : "—"}</span>
      </span>
    </button>
  );
}

const DIGEST_LABEL: Record<DigestStatus, { text: string; tone: string }> = {
  verified: { text: "✓ digest verified", tone: "success" },
  mismatch: { text: "✗ digest mismatch", tone: "danger" },
  pending: { text: "verifying digest…", tone: "muted" },
  none: { text: "no check digest", tone: "warning" },
  error: { text: "digest unavailable", tone: "warning" },
};

function LevelPreview({
  level,
  palette,
  answer,
  stats,
  onClose,
}: {
  level: Level;
  palette: string;
  answer: AnswerValue | null;
  stats: PackResponse["stats"][string] | undefined;
  onClose: () => void;
}) {
  const [reveal, setReveal] = useState(true);
  useHotkey("r", () => setReveal((v) => !v));
  const status = useDigestVerification(level.spec, answer);
  const label = answer ? DIGEST_LABEL[status] : { text: "answer not recovered", tone: "danger" };

  return (
    <aside className={styles.levelPreview} aria-label={`Level ${level.n} preview`}>
      <div className={`${styles.levelPreviewHead} ${tileClass(palette)}`}>
        <div>
          <span className="monoLabel">Level</span>
          <div className={styles.levelPreviewN}>{level.n}</div>
        </div>
        <div className={styles.levelPreviewHeadRight}>
          <span className="mono">{level.spec.id}</span>
          <span className="mono">{level.spec.type}</span>
          <span className={styles.flagRow}>
            {(level.flags ?? []).map((f) => (
              <Chip key={f} tone={f === "gate" ? "danger" : f === "relief" ? "success" : f === "intro" ? "info" : "neutral"}>
                {f}
              </Chip>
            ))}
          </span>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close preview" className={styles.closeBtn}>
          ✕
        </Button>
      </div>
      <div className={styles.levelPreviewBody}>
        <div className={styles.digestRow}>
          <Chip tone={label.tone}>{label.text}</Chip>
          <Button size="sm" variant={reveal ? "primary" : "secondary"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R">
            {reveal ? "Hide answer" : "Reveal answer"}
          </Button>
        </div>
        <div className={styles.stage}>
          <PuzzleView spec={level.spec} answer={answer} reveal={reveal} />
        </div>
        <div className={styles.answerLine}>
          <span className="monoLabel">Answer</span>
          {answer ? (
            <span className={styles.answerValue}>
              <span className="mono">{answer.kind}</span>
              <code>{canonicalAnswer(answer)}</code>
            </span>
          ) : (
            <span className={styles.faint}>not recovered</span>
          )}
        </div>
        <div className={styles.answerLine}>
          <span className="monoLabel">check</span>
          <code className={styles.digest} title={level.spec.check?.digest}>
            {level.spec.check?.digest ?? "—"}
          </code>
        </div>
        <div className={styles.statsRow}>
          <Stat label="Difficulty" value={level.spec.difficulty.score.toFixed(2)} sub={`tier ${level.spec.difficulty.tier ?? "—"}`} />
          <Stat label="Completions" value={fmtInt(stats?.completions)} />
          <Stat label="Avg stars" value={stats ? stats.avgStars.toFixed(2) : "—"} sub="of 3" />
          <Stat label="Attempts" value={stats ? stats.avgAttempts.toFixed(2) : "—"} sub={`budget ${fmtMs(level.spec.timeBudgetMs)}`} />
        </div>
      </div>
    </aside>
  );
}
